import { useState, useEffect } from "react";
import { Box, Typography, Skeleton, Tooltip } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import apiService from "../../../service/Api";
import {
  getCache,
  setCache,
  type ComponentManager,
  type Developer,
} from "./AssignmentsCache";

const deriveComponentName = (pathname: string) => {
  const segments = pathname.split("/").filter(Boolean);
  return segments.slice(0, 2).join("/");
};

const getDeveloperName = (dev?: Developer) => {
  if (!dev) return "Not Assigned";
  const full = [dev.first_name, dev.last_name].filter(Boolean).join(" ");
  return full || dev.username;
};

const normalise = (s: string) => s.toLowerCase().replace(/[\s\-_/]/g, "");

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [managers, setManagers] = useState<ComponentManager[]>(
    getCache() || [],
  );
  const [loading, setLoading] = useState(!getCache());

  useEffect(() => {
    const cached = getCache();
    if (cached) {
      setManagers(cached);
      setLoading(false);
      return;
    }
    const fetchManagers = async () => {
      setLoading(true);
      try {
        const response = await apiService.get<
          { results?: ComponentManager[] } | ComponentManager[]
        >("/api/configurations/components-managers/");
        const data = Array.isArray(response.data)
          ? response.data
          : response.data.results || [];
        setCache(data);
        setManagers(data);
      } catch (error) {
        console.error("Failed to fetch component managers:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchManagers();
  }, []);

  // Listen for assignments-updated event dispatched by UpdateFooter after mutations
  useEffect(() => {
    const handleUpdate = async () => {
      setLoading(true);
      try {
        const response = await apiService.get<
          { results?: ComponentManager[] } | ComponentManager[]
        >("/api/configurations/components-managers/");
        const data = Array.isArray(response.data)
          ? response.data
          : response.data.results || [];
        setCache(data);
        setManagers(data);
      } catch (error) {
        console.error("Failed to refresh component managers:", error);
      } finally {
        setLoading(false);
      }
    };

    window.addEventListener("assignments-updated", handleUpdate);
    return () =>
      window.removeEventListener("assignments-updated", handleUpdate);
  }, []);

  const componentName = deriveComponentName(location.pathname);
  const match = managers.find(
    (m) => normalise(m.component.name) === normalise(componentName),
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1,
          borderTop: "1px solid #e0e0e0",
          mt: 2,
          backgroundColor: "#f9f9f9",
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Skeleton width={160} height={20} />
          <Skeleton width={300} height={16} />
        </Box>
      </Box>
    );
  }

  const handleClick = () => {
    navigate("/configurations/components-manager", {
      state: {
        componentName: match?.component.name ?? componentName,
        componentId: match?.component.id ?? null,
      },
    });
  };

  return (
    <Tooltip
      title={
        match
          ? "Click to view or edit this component's assignment"
          : "Click to assign a lead to this component"
      }
      placement="top"
    >
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1,
          borderTop: "1px solid #e0e0e0",
          mt: 2,
          backgroundColor: "#f9f9f9",
          cursor: "pointer",
          transition: "background-color 0.2s",
          "&:hover": {
            backgroundColor: "#f0f0f0",
          },
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: "bold" }}>
            {match?.component.name ?? componentName}
          </Typography>
          <Typography variant="body2">
            {match
              ? `Lead: ${getDeveloperName(match.lead_developer)} | Backup: ${getDeveloperName(match.backup_developer)}`
              : "Not assigned — click to assign a lead"}
          </Typography>
        </Box>
        {match?.version && (
          <Typography variant="caption" color="text.secondary">
            v{match.version}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default Footer;
